import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { PresentationSlide, SlideObject, resolveSlideFontFamily, resolvedFontWeight, resolvedLineHeight, slideSize } from '../types';
import { mediaRadiusSx, objectPreviewHtml, objectSurfaceSx, resolveMediaUrl, textFlowSx } from './slidePreview';
import { imageCropStyle } from './objectTransform';
import { isFillableShape } from './shapeFrame';
import { objectEffectSx } from './objectEffects';
import ShapeObjectView from './shapeObjectView';
import ChartObjectView from './ChartObjectView';
import TableObjectView from './TableObjectView';

export interface StaticSlideViewProps {
  slide: PresentationSlide;
  onClick?: () => void;
  blackout?: boolean;
  framed?: boolean;
  variant?: 'fit' | 'print';
  rootRef?: React.Ref<HTMLDivElement>;
  sx?: SxProps<Theme>;
  contentWidth?: number;
  contentHeight?: number;
  pageFormat?: string | null;
}

function objectBoxSx(object: SlideObject): SxProps<Theme> {
  return {
    position: 'absolute',
    left: object.x,
    top: object.y,
    width: object.w,
    height: object.h,
    zIndex: object.z,
    overflow: object.type === 'text' || object.type === 'shape' ? 'visible' : 'hidden',
    color: object.style?.color || '#111111',
    fontFamily: resolveSlideFontFamily(object.style?.fontFamily),
    fontSize: object.style?.fontSize || 24,
    fontWeight: resolvedFontWeight(object.style),
    fontStyle: object.style?.fontStyle === 'italic' ? 'italic' : undefined,
    textDecoration: object.style?.textDecoration === 'underline' ? 'underline' : undefined,
    textAlign: object.style?.textAlign || 'left',
    ...(object.type === 'text' || isFillableShape(object) ? { overflowWrap: 'break-word' } : {}),
    lineHeight: resolvedLineHeight(object.style),
    ...(object.style?.letterSpacing != null ? { letterSpacing: `${object.style.letterSpacing}px` } : {}),
    textTransform: object.style?.textTransform || 'none',
    ...(object.type === 'text' ? { display: 'flex', flexDirection: 'column' } : {}),
    justifyContent: object.style?.verticalAlign === 'middle' ? 'center' : object.style?.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
    opacity: object.style?.opacity ?? 1,
    ...(object.style?.rotation ? { transform: `rotate(${object.style.rotation}deg)` } : {}),
    transformOrigin: 'center center',
    pointerEvents: 'none',
    ...(object.type === 'text' ? textFlowSx : {}),
    ...objectSurfaceSx(object),
    ...objectEffectSx(object),
  } as SxProps<Theme>;
}

function SlideObjects({ objects }: { objects: SlideObject[] }) {
  return (
    <>
      {objects.map((object) => (
        <Box key={object.id} sx={objectBoxSx(object)}>
          {object.type === 'text' ? (
            <Box sx={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: objectPreviewHtml(object) }} />
          ) : object.type === 'table' ? (
            <TableObjectView object={object} />
          ) : object.type === 'chart' ? (
            <ChartObjectView object={object} />
          ) : object.type === 'shape' ? (
            <ShapeObjectView object={object}>
              {object.content ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: object.style?.verticalAlign === 'middle' ? 'center' : object.style?.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
                    boxSizing: 'border-box',
                    p: `${object.style?.padding || 0}px`,
                    ...textFlowSx,
                  }}
                  dangerouslySetInnerHTML={{ __html: objectPreviewHtml(object) }}
                />
              ) : null}
            </ShapeObjectView>
          ) : object.imageUrl ? (
            <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', ...mediaRadiusSx(object) }}>
              <Box
                component="img"
                src={resolveMediaUrl(object.imageUrl)}
                alt=""
                sx={{ display: 'block', ...imageCropStyle(object.style) }}
              />
            </Box>
          ) : null}
        </Box>
      ))}
    </>
  );
}

export function areStaticSlideViewPropsEqual(
  prev: StaticSlideViewProps,
  next: StaticSlideViewProps,
): boolean {
  return prev.slide === next.slide
    && prev.onClick === next.onClick
    && prev.blackout === next.blackout
    && prev.framed === next.framed
    && prev.variant === next.variant
    && prev.rootRef === next.rootRef
    && prev.sx === next.sx
    && prev.contentWidth === next.contentWidth
    && prev.contentHeight === next.contentHeight
    && prev.pageFormat === next.pageFormat;
}

const MemoStaticSlideView = React.memo(function StaticSlideViewInner({
  slide, onClick, blackout, framed, variant = 'fit', rootRef, sx, contentWidth, contentHeight, pageFormat,
}: StaticSlideViewProps) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const formatSize = slideSize(pageFormat);
  const canvasWidth = contentWidth && contentWidth > 0 ? contentWidth : formatSize.width;
  const canvasHeight = contentHeight && contentHeight > 0 ? contentHeight : formatSize.height;
  const fillParent = Boolean(contentWidth && contentHeight);
  const [scale, setScale] = useState(variant === 'print' ? 1 : 0.08);

  useLayoutEffect(() => {
    if (variant === 'print') return;
    const frame = frameRef.current;
    if (!frame) return;
    const update = () => {
      const next = Math.min(frame.clientWidth / canvasWidth, frame.clientHeight / canvasHeight);
      setScale(Number.isFinite(next) && next > 0 ? next : 0.08);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [slide.slideId, variant, canvasWidth, canvasHeight]);

  const objects = [...slide.objects].sort((a, b) => (a.z || 0) - (b.z || 0));

  if (variant === 'print') {
    return (
      <Box
        ref={rootRef}
        data-slide-print="true"
        sx={{
          width: canvasWidth,
          height: canvasHeight,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: slide.backgroundColor || '#FFFFFF',
          ...sx,
        }}
      >
        <SlideObjects objects={objects} />
      </Box>
    );
  }

  return (
    <Box
      ref={frameRef}
      onClick={onClick}
      sx={{
        width: '100%',
        height: fillParent ? '100%' : undefined,
        aspectRatio: fillParent ? undefined : formatSize.aspectRatio,
        position: fillParent ? 'absolute' : 'relative',
        inset: fillParent ? 0 : undefined,
        bgcolor: slide.backgroundColor || '#FFFFFF',
        overflow: 'hidden',
        borderRadius: framed ? 1 : 0,
        boxShadow: framed ? '0 12px 40px rgba(0,0,0,0.35)' : 'none',
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
    >
      <Box
        sx={{
          width: canvasWidth,
          height: canvasHeight,
          position: 'absolute',
          left: fillParent ? '50%' : 0,
          top: fillParent ? '50%' : 0,
          transform: fillParent
            ? `translate(-50%, -50%) scale(${scale})`
            : `scale(${scale})`,
          transformOrigin: fillParent ? 'center center' : 'top left',
        }}
      >
        <SlideObjects objects={objects} />
      </Box>
      {blackout && <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#000' }} />}
    </Box>
  );
}, areStaticSlideViewPropsEqual);

function StaticSlideView(props: StaticSlideViewProps) {
  return <MemoStaticSlideView {...props} />;
}

export default StaticSlideView;
